import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Receipt, TrendDown, ArrowRight } from "@phosphor-icons/react";
import api from "../api";

/**
 * Compact dashboard widget that mirrors RevenueWidget.
 * Shows: Total Expenses (window), top categories, and Net = Revenue - Expenses.
 *
 * Props:
 *  - refreshTick: bump to force re-fetch
 *  - revenueTotal: pre-fetched total revenue for the same window (parent already
 *      hits /revenue for the Revenue widget; we reuse it for the Net line).
 *  - window: '7d' | '30d' | '90d' | '365d' (default '30d')
 */
export default function ExpensesWidget({ refreshTick = 0, window: win = "30d" }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/expenses/summary?window=${win}`).then((r) => r.data).catch(() => null),
      api.get(`/revenue?window=${win}`).then((r) => r.data).catch(() => null),
    ]).then(([exp, rev]) => {
      if (!alive) return;
      setData(exp || { total: 0, by_category: [], count: 0 });
      setRevenue(rev?.total_revenue || 0);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshTick, win]);

  const total = data?.total ?? 0;
  const net = revenue - total;
  const topCats = (data?.by_category || []).slice(0, 4);
  const grandCat = topCats.reduce((s, c) => s + c.amount, 0) || 1;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-3"
      data-testid="expenses-widget"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 flex items-center justify-center">
            <Receipt size={16} weight="bold" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("expenses.widget_title")}</p>
            <p className="text-[11px] text-zinc-500">{t(`revenue.window_${win}`, win)}</p>
          </div>
        </div>
        <Link
          to="/expenses"
          className="text-xs font-medium text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
          data-testid="expenses-widget-link"
        >
          {t("common.view_all")} <ArrowRight size={11} weight="bold" />
        </Link>
      </div>

      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-3xl font-bold tabular-nums text-zinc-900" data-testid="expenses-widget-total">
          ${total.toFixed(2)}
        </span>
        <span className="text-xs text-zinc-500">{t("expenses.count_n", { n: data?.count || 0 })}</span>
      </div>

      <div className="pt-2 border-t border-zinc-100 flex items-center justify-between">
        <span className="text-xs text-zinc-600 inline-flex items-center gap-1.5">
          <TrendDown size={12} weight="bold" /> {t("expenses.net_revenue")}
        </span>
        <span
          className={`text-sm font-semibold tabular-nums ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}
          data-testid="expenses-widget-net"
        >
          ${net.toFixed(2)}
        </span>
      </div>

      {loading ? (
        <p className="text-xs text-zinc-400">{t("common.loading")}</p>
      ) : topCats.length === 0 ? (
        <p className="text-xs text-zinc-500">{t("expenses.widget_empty")}</p>
      ) : (
        <div className="space-y-1.5 pt-1">
          {topCats.map((c) => {
            const pct = Math.round((c.amount / grandCat) * 100);
            return (
              <div key={c.category} className="flex items-center gap-2 text-xs" data-testid={`expense-cat-${c.category.toLowerCase()}`}>
                <span className="w-20 text-zinc-600 shrink-0">{t(`expenses.cat_${c.category.toLowerCase().replace(/ /g, "_")}`, c.category)}</span>
                <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-rose-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="font-semibold tabular-nums text-zinc-800 w-16 text-right">${c.amount.toFixed(2)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
